import CustomLoader from "app/components/common/CustomLoader";
import CommonTable1 from "app/components/Tables/CommonTable1";
import {
  deleteWA_Template,
  getWA_TemplateById,
  updateWA_Template,
  map_Wa_TemplateWithPurpose,
} from "app/services/schoolReportingServices/communicationSetting.service";
import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import DeleteConfirmationDialog from "app/components/common/Dialogs/DeleteConfirmationDialog";
import { ViewTemplateDialog, EditTemplateDialog } from "./Dialogs";
import TableActionButtons from "app/components/common/tableComponent/TableActionButtons";

const WhatsAppPurpose = () => {
  const [state, setState] = useState({
    viewDialog: false,
    editDialog: false,
    deleteDialog: false,
    selectedTemplate: null,
    viewData: {},
  });

  const {
    data: WhatsAppPurposeData,
    refetch: refetchWhatsAppPurpose,
    loading: purposeDataLoading,
    isFetching: purposeDataFetching,
  } = useQuery({
    queryKey: ["getWhatsAppPurpose"],
    queryFn: () => getWA_TemplateById(),
    select: (data) => data?.data?.list,
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  // Get template by ID for viewing or editing
  useQuery({
    queryKey: ["getWA_TemplateById", state.selectedTemplate],
    queryFn: () => getWA_TemplateById(state.selectedTemplate),
    enabled: !!state.selectedTemplate && (state.viewDialog || state.editDialog),
    onSuccess: (data) => {
      setState((prev) => ({
        ...prev,
        viewData: data?.data?.obj || {},
      }));
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  // Delete mutation
  const { mutate: deleteTemplateMutate, isLoading: deleteLoading } =
    useMutation(deleteWA_Template, {
      onSuccess: (data) => {
        toast.success(data?.data?.message || "Template deleted successfully");
        setState((prev) => ({
          ...prev,
          deleteDialog: false,
          selectedTemplate: null,
        }));
        refetchWhatsAppPurpose();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message || "Failed to delete template"
        );
      },
    });

  // Create/Update mutation - handles both first-time mapping and updates
  const { mutate: saveTemplateMutate, isLoading: saveLoading } =
    useMutation(
      ({ id, data }) => {
        // If ID exists, update the mapping; otherwise, create new mapping
        if (id) {
          return updateWA_Template(id, data);
        } else {
          return map_Wa_TemplateWithPurpose(data);
        }
      },
      {
        onSuccess: (data) => {
          toast.success(
            data?.data?.message || 
            state.selectedTemplate 
              ? "Template updated successfully" 
              : "Template mapped successfully"
          );
          setState((prev) => ({
            ...prev,
            editDialog: false,
            selectedTemplate: null,
            viewData: {},
          }));
          refetchWhatsAppPurpose();
        },
        onError: (error) => {
          toast.error(
            error?.response?.data?.message || "Failed to save template"
          );
        },
      }
    );

  // Handlers
  const handleView = (rowData) => {
    setState((prev) => ({
      ...prev,
      selectedTemplate: rowData._id,
      viewDialog: true,
    }));
  };

  const handleEdit = (rowData) => {
    console.log("Clicked");
    
    setState((prev) => ({
      ...prev,
      // Only set selectedTemplate if a template is already mapped (has _id from mapping)
      selectedTemplate: rowData.name ? rowData._id : null,
      editDialog: true,
      viewDialog: false,
      viewData: rowData, // Set viewData directly for new mappings
    }));
  };

  const handleDelete = (rowData) => {
    setState((prev) => ({
      ...prev,
      selectedTemplate: rowData._id,
      deleteDialog: true,
    }));
  };

  const handleCloseViewDialog = () => {
    setState((prev) => ({
      ...prev,
      viewDialog: false,
      selectedTemplate: null,
      viewData: {},
    }));
  };

  const handleCloseDeleteDialog = () => {
    setState((prev) => ({
      ...prev,
      deleteDialog: false,
      selectedTemplate: null,
    }));
  };

  const handleConfirmDelete = () => {
    if (state.selectedTemplate) {
      deleteTemplateMutate(state.selectedTemplate);
    }
  };

  const handleUpdateTemplate = (saveData) => {
    if (state.selectedTemplate) {
      // Update existing mapping
      saveTemplateMutate({
        id: state.selectedTemplate,
        data: saveData,
      });
    } else {
      // First-time mapping (no ID)
      saveTemplateMutate({
        id: null,
        data: saveData,
      });
    }
  };
  const columns = useMemo(() => {
    return [
      {
        header: "Sr.No",
        Cell: ({ cell }) => (
          <p
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {parseInt(cell.row.id) + 1}
          </p>
        ),
        size: 30,
      },
      {
        header: "Purpose",
        accessorKey: "purpose",
        size: 60,
      },
      {
        header: "Template Name",
        accessorKey: "name",
        size: 60,
      },
      {
        header: "Total Parameters",
        accessorKey: "num_of_parameters",
        size: 60,
      },
      {
        header: "Status",
        accessorKey: "active",
        Cell: ({ cell }) => {
          return <p>{cell.getValue() ? "Active" : "Inactive"}</p>;
        },
        size: 60,
      },
      {
        header: "Actions",
        Cell: ({ cell }) => {
          const hasTemplate = cell.row.original.name; // Check if template is mapped
          
          return (
            <>
            <TableActionButtons 
            showEdit
            showDelete={hasTemplate} // Only show delete if template is mapped
            showView={hasTemplate}   // Only show view if template is mapped
            handleDelete={() => handleDelete(cell.row.original)}
            handleEdit={() => handleEdit(cell.row.original)}
            handleView={() => handleView(cell.row.original)}
            />
              </>
          );
        },
        size: 60,
      },
    ];
  }, []);
  return (
    <>
      <Helmet>
        <title>WhatsApp Purpose</title>
      </Helmet>

      {/* View Dialog */}
      <ViewTemplateDialog
        open={state.viewDialog}
        onClose={handleCloseViewDialog}
        viewData={state.viewData}
      />

      {/* Edit Dialog */}
      <EditTemplateDialog
        open={state.editDialog}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            editDialog: false,
            selectedTemplate: null,
          }))
        }
        editData={state.viewData}
        onUpdate={handleUpdateTemplate}
        loading={saveLoading}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={state.deleteDialog}
        onClose={handleCloseDeleteDialog}
        handleDelete={handleConfirmDelete}
        loading={deleteLoading}
      />

      <CustomLoader show={purposeDataLoading || purposeDataFetching} />
      {/* <Stack direction="row" justifyContent="flex-end" gap={1}>
        <Button variant="contained" color="primary">
          +New Subject Group
        </Button>
      </Stack> */}
      <CommonTable1
        data={WhatsAppPurposeData}
        columns={columns}
        loading={purposeDataLoading || purposeDataFetching}
      />
    </>
  );
};

export default WhatsAppPurpose;
